import { useCallback, useEffect, useRef } from "react";
import { ACTIVITY_EVENTS } from "../constants";

type InactivityDetectorOptions = {
  interval: number;
  maxCallCount?: number;
  onInactive: () => void;
  onActive?: () => void;
  autoStart?: boolean;
};

type InactivityDetectorControls = {
  triggerUserInactivityMonitoring: () => void;
  stopMonitoring: () => void;
  resetTimer: () => void;
};

export function useUserInactivityDetector({
  interval,
  maxCallCount = Infinity,
  onInactive,
  onActive,
  autoStart = true
}: InactivityDetectorOptions): InactivityDetectorControls {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStartedRef = useRef(false);
  const remainingCallsRef = useRef(maxCallCount);
  const optionsRef = useRef({ interval, maxCallCount });
  const onInactiveRef = useRef(onInactive);
  const onActiveRef = useRef(onActive);

  optionsRef.current = { interval, maxCallCount };

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    if (!isStartedRef.current) return;
    clearTimer();
    if (remainingCallsRef.current <= 0) return;

    timerRef.current = setTimeout(() => {
      onInactiveRef.current?.();
      remainingCallsRef.current -= 1;
      if (remainingCallsRef.current > 0) {
        schedule();
      }
    }, optionsRef.current.interval);
  }, [clearTimer]);

  // Stable event handler - same reference for add/remove
  const handleActivity = useCallback(
    (event: Event) => {
      const isTrusted = (event as any)?.isTrusted ?? true;
      if (!isTrusted) return;

      onActiveRef.current?.();
      schedule();
    },
    [schedule]
  );

  const addListeners = useCallback(() => {
    ACTIVITY_EVENTS.forEach(name => {
      document.addEventListener(name, handleActivity, { passive: true });
    });
  }, [handleActivity]);

  const removeListeners = useCallback(() => {
    ACTIVITY_EVENTS.forEach(name => {
      document.removeEventListener(name, handleActivity);
    });
  }, [handleActivity]);

  const start = useCallback(() => {
    if (isStartedRef.current) return;

    isStartedRef.current = true;
    remainingCallsRef.current = optionsRef.current.maxCallCount;
    schedule();
    addListeners();
  }, [schedule, addListeners]);

  const stop = useCallback(() => {
    if (!isStartedRef.current) return;

    clearTimer();
    removeListeners();
    isStartedRef.current = false;
    remainingCallsRef.current = optionsRef.current.maxCallCount;
  }, [clearTimer, removeListeners]);

  const reset = useCallback(() => {
    if (!isStartedRef.current) return;

    clearTimer();
    remainingCallsRef.current = optionsRef.current.maxCallCount;
    schedule();
  }, [clearTimer, schedule]);

  useEffect(() => {
    onInactiveRef.current = onInactive;
  }, [onInactive]);

  useEffect(() => {
    onActiveRef.current = onActive;
  }, [onActive]);

  useEffect(() => {
    if (isStartedRef.current) {
      clearTimer();
      schedule();
    }
  }, [interval, clearTimer, schedule]);

  useEffect(() => {
    if (autoStart) {
      start();
    }
    return () => {
      clearTimer();
      removeListeners();
      isStartedRef.current = false;
    };
  }, [autoStart, start, clearTimer, removeListeners]);

  return { triggerUserInactivityMonitoring: start, stopMonitoring: stop, resetTimer: reset };
}
